import Product from './Product';
import type { IProduct } from './Product';
import CartItem from './CartItem';

interface ICartProduct extends IProduct {
  readonly isSelected?: boolean;
  readonly isSoldOut?: boolean;
  readonly isPartiallySoldOut?: boolean;
}

/**
 * A product is a group of different CartItems with different SKU ids that
 * are conceptually the same product, but have different serving options.
 *
 * See the Product class for more details.
 */
class CartProduct extends Product<CartItem> implements ICartProduct {
  /**
   * Returns the item in items[0] if there is only one item in the array (i.e.,
   * if there are no more serving options available).
   *
   * If there are more items, it tries to find, amongst those that are not sold
   * out, a selected one (quantity > 0) or the one corresponding to the default
   * servings in the plan, falling back to availableItems[0] if it can't.
   *
   * If all the items are sold out, returns the default servings one even if
   * it's sold out or fallbacks to items[0] if it can't.
   */
  get currentItem(): CartItem {
    if (!this.hasServingOptions) return this.items[0];

    const availableItems = this.items.filter((item) => !item.isSoldOut);

    if (!availableItems.length) {
      return this.items.find(({ sku }) => sku.servings === this.defaultServings)
        || this.items[0];
    }

    return availableItems.find((item) => item.isSelected)
      || availableItems.find(({ sku }) => sku.servings === this.defaultServings)
      || availableItems[0];
  }

  get isSelected(): boolean {
    return this.items.some((item) => item.isSelected);
  }

  get isSoldOut(): boolean {
    return this.items.every((item) => item.isSoldOut);
  }

  get isPremiumItem(): boolean {
    return this.items.every((item) => item.premium);
  }
  get isPartiallySoldOut(): boolean {
    return this.items.some((item) => item.isSoldOut);
  }

  get soldOutServings(): number[] {
    // @ts-expect-error TODO: fix null safety
    return this.items
      .filter((item) => item.isSoldOut)
      .map((item) => item.sku.servings);
  }
}

export default CartProduct;
